import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '@/types/user';
import { authApi } from '@/features/auth/authApi';
import type { EnglishLevelId } from '@/constant/EnglishLevels';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  hasRole: (roles: string[]) => boolean;
  defaultReadLevel: EnglishLevelId;
  updateName: (name: string) => void;
  setDefaultReadLevel: (level: EnglishLevelId) => void;
  updateProfile: (name: string, level: EnglishLevelId) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [defaultReadLevel, setDefaultReadLevel] = useState<EnglishLevelId>(() => {
    const stored = localStorage.getItem('defaultReadLevel') as EnglishLevelId | null;
    return stored === 'A' || stored === 'B' || stored === 'C' ? stored : 'A';
  });

  useEffect(() => {
    const bootstrapAuth = async (): Promise<void> => {
      // Check for stored auth token on mount
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');

      if (!storedToken || !storedUser) {
        setIsLoading(false);
        return;
      }

      setToken(storedToken);

      let parsedUser: User;
      try {
        parsedUser = JSON.parse(storedUser) as User;
      } catch {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setUser(null);
        setToken(null);
        setIsLoading(false);
        return;
      }

      setUser(parsedUser);
      if (parsedUser.defaultReadLevel) {
        setDefaultReadLevel(parsedUser.defaultReadLevel);
      }

      try {
        const meResponse = await authApi.getCurrentUser();
        if (meResponse?.success && meResponse?.data) {
          const refreshedUser: User = {
            ...parsedUser,
            ...meResponse.data,
            // Preserve provider hint from existing session if /me does not include it.
            authProvider: parsedUser.authProvider,
          };
          setUser(refreshedUser);
          localStorage.setItem('user', JSON.stringify(refreshedUser));
          if (refreshedUser.defaultReadLevel) {
            setDefaultReadLevel(refreshedUser.defaultReadLevel);
            localStorage.setItem('defaultReadLevel', refreshedUser.defaultReadLevel);
          }
        }
      } catch {
        // Ignore hydration failure and keep stored session; protected calls will handle invalid tokens.
      } finally {
        setIsLoading(false);
      }
    };

    void bootstrapAuth();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      // Clear any previous auth data before attempting login
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      setUser(null);
      setToken(null);

      const response = await authApi.login(email, password);

      // Validate response structure
      if (!response.success || !response.data || !response.data.token || !response.data.user) {
        throw new Error('Invalid login response from server');
      }

      setUser(response.data.user);
      setToken(response.data.token);
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      if (response.data.user.defaultReadLevel) {
        setDefaultReadLevel(response.data.user.defaultReadLevel);
        localStorage.setItem('defaultReadLevel', response.data.user.defaultReadLevel);
      }
    } catch (error) {
      // Ensure auth state is cleared on error
      setUser(null);
      setToken(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    // Reset to default 'A' on logout per spec
    setDefaultReadLevel('A');
    localStorage.setItem('defaultReadLevel', 'A');
  };

  const updateName = (name: string): void => {
    if (!user) return;
    const updatedUser: User = { ...user, name };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const setDefaultReadLevelCtx = (level: EnglishLevelId): void => {
    setDefaultReadLevel(level);
    localStorage.setItem('defaultReadLevel', level);
    setUser(prev => (prev ? { ...prev, defaultReadLevel: level } : prev));
  };

  const hasRole = (roles: string[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const updateProfile = async (name: string, level: EnglishLevelId): Promise<void> => {
    if (!user) return;
    const payload: { name?: string; defaultReadLevel?: EnglishLevelId } = {};
    if (name && name.trim() && name.trim() !== user.name) {
      payload.name = name.trim();
    }
    if (level !== defaultReadLevel) {
      payload.defaultReadLevel = level;
    }

    // If nothing changed, skip API call
    if (!payload.name && !payload.defaultReadLevel) {
      return;
    }

    const response = await authApi.updateCurrentUser(payload);
    if (!response.success || !response.data) {
      throw new Error('Failed to update profile');
    }

    const updatedUser: User = {
      ...user,
      name: response.data.name,
      defaultReadLevel: response.data.defaultReadLevel,
      updatedAt: response.data.updatedAt,
    };
    setUser(updatedUser);
    setDefaultReadLevel(response.data.defaultReadLevel || level);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    if (response.data.defaultReadLevel) {
      localStorage.setItem('defaultReadLevel', response.data.defaultReadLevel);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasRole,
    defaultReadLevel,
    updateName,
    setDefaultReadLevel: setDefaultReadLevelCtx,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
